import React, { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import {
  Loader2, Wallet, Users, DollarSign, TrendingUp, Briefcase, Building2,
  ArrowDownToLine, Banknote, BarChart3, Percent, CheckCircle, Clock, X,
  ArrowUpRight, Zap, Play, Target, AlertCircle, Shield, Settings, ChevronDown,
  Plus, UserCheck, UserX
} from "lucide-react";
import { Card, GoldButton } from "../shared/UIComponents";
import { fmtUSD } from "../../utils/currency";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
  raising: "bg-blue-100 text-blue-700 border-blue-200",
  deploying: "bg-emerald-100 text-emerald-700 border-emerald-200",
  harvesting: "bg-amber-100 text-amber-700 border-amber-200",
  closed: "bg-gray-100 text-gray-600 border-gray-200",
};

const LOAN_TYPE_LABELS = {
  fix_flip: "Fix & Flip", bridge: "Bridge", dscr: "DSCR", cash_out: "Cash-Out Refi",
  construction: "Construction", ground_up: "Ground Up", multifamily: "Multifamily", commercial: "Commercial",
};

export default function FundDashboard({ onEntityNavigate }) {
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedFund, setSelectedFund] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [showCallModal, setShowCallModal] = useState(false);
  const [showAllocateModal, setShowAllocateModal] = useState(false);
  const [callPct, setCallPct] = useState(25);
  const [actionLoading, setActionLoading] = useState(false);

  const token = localStorage.getItem("ic_token");
  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/funds/admin/overview`, { headers });
      setOverview(res.data);
      if (res.data.funds?.length > 0 && !selectedFund) {
        setSelectedFund(res.data.funds[0]);
      } else if (selectedFund) {
        const updated = res.data.funds.find(f => f.fund_id === selectedFund.fund_id);
        if (updated) setSelectedFund(updated);
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const issueCapitalCall = async () => {
    if (!selectedFund) return;
    setActionLoading(true);
    try {
      const res = await axios.post(`${API}/funds/capital-call`, {
        fund_id: selectedFund.fund_id,
        call_pct: callPct,
      }, { headers });
      alert(`Capital call issued: ${fmtUSD(res.data.total_called)} called from ${res.data.investor_count} investors`);
      setShowCallModal(false);
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to issue capital call");
    }
    finally { setActionLoading(false); }
  };

  const updateFundStatus = async (status) => {
    if (!selectedFund) return;
    try {
      await axios.put(`${API}/funds/${selectedFund.fund_id}/status`, { status }, { headers });
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to update status");
    }
  };

  const toggleAutoAllocate = async () => {
    if (!selectedFund) return;
    try {
      await axios.put(`${API}/funds/${selectedFund.fund_id}/settings`, {
        auto_allocate_enabled: !selectedFund.auto_allocate_enabled,
      }, { headers });
      fetchData();
    } catch (err) { alert(err.response?.data?.detail || "Failed"); }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-emerald-700" /></div>;

  const s = overview?.summary || {};
  const funds = overview?.funds || [];
  const fund = selectedFund;

  return (
    <div className="space-y-6" data-testid="fund-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-bold text-[#0A0A0A] flex items-center gap-2">
            <Building2 className="h-5 w-5 text-gold-dark" /> Fund Management
          </h3>
          <p className="text-xs text-[#888] mt-0.5">Manage private credit funds, subscriptions, capital calls, and distributions</p>
        </div>
        <GoldButton onClick={() => setShowCreate(true)} data-testid="create-fund-btn">
          <Plus className="h-4 w-4 inline mr-1" /> New Fund
        </GoldButton>
      </div>

      {/* Summary KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: "Total Funds", value: s.total_funds, icon: Building2, color: "text-emerald-600" },
          { label: "Total AUM", value: `$${((s.total_aum || 0) / 1e6).toFixed(1)}M`, icon: Wallet, color: "text-blue-600" },
          { label: "Deployed", value: `$${((s.total_deployed || 0) / 1e6).toFixed(1)}M`, icon: TrendingUp, color: "text-gold" },
          { label: "Pending Subs", value: s.pending_subscriptions, icon: Clock, color: "text-amber-600" },
        ].map((kpi) => {
          const Icon = kpi.icon;
          return (
            <Card hover={false} key={kpi.label}>
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-cream border border-cream-warm"><Icon className={`h-4 w-4 ${kpi.color}`} /></div>
                <div>
                  <div className="text-[9px] text-[#999] uppercase tracking-wider font-semibold">{kpi.label}</div>
                  <div className={`text-xl font-black ${kpi.color}`}>{kpi.value}</div>
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Fund Selector */}
      {funds.length > 0 && (
        <div className="flex gap-2 flex-wrap">
          {funds.map((f) => (
            <button key={f.fund_id} onClick={() => setSelectedFund(f)}
              data-testid={`fund-select-${f.fund_id}`}
              className={`px-4 py-2 rounded-lg text-sm font-medium border transition-all ${
                fund?.fund_id === f.fund_id
                  ? "bg-[#022c22] text-gold border-[#022c22]"
                  : "bg-white text-[#555] border-cream-warm hover:border-[#022c22]/30"
              }`}>
              {f.name} <span className={`ml-2 px-1.5 py-0.5 rounded text-[9px] uppercase font-bold border ${STATUS_COLORS[f.status]}`}>{f.status}</span>
            </button>
          ))}
        </div>
      )}

      {/* Selected Fund Detail */}
      {fund && (
        <>
          {/* Fund Hero */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            className="rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-900 to-emerald-950 p-6 shadow-lg" data-testid="fund-hero">
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-xl bg-white/10 border border-white/20"><Building2 className="h-5 w-5 text-emerald-300" /></div>
                <div>
                  <h3 className="text-lg font-bold text-white">{fund.name}</h3>
                  <p className="text-xs text-white/50">Vintage {fund.vintage_year} | {fund.fund_term_years}yr Term | {fund.preferred_return_pct}% Pref | {fund.carried_interest_pct}% Carry</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase border ${STATUS_COLORS[fund.status]}`}>{fund.status}</span>
                <select value={fund.status} onChange={(e) => updateFundStatus(e.target.value)} data-testid="fund-status-select"
                  className="bg-white/10 border border-white/20 text-white text-xs rounded-lg px-2 py-1 outline-none">
                  {["raising", "deploying", "harvesting", "closed"].map(s => <option key={s} value={s} className="text-black">{s}</option>)}
                </select>
              </div>
            </div>

            {/* Subscription Progress */}
            <div className="mb-5">
              <div className="flex justify-between text-xs text-white/60 mb-1">
                <span>Subscriptions: ${(fund.total_committed || 0).toLocaleString()}</span>
                <span>Target: ${(fund.target_size || 0).toLocaleString()}</span>
              </div>
              <div className="w-full bg-white/10 rounded-full h-3">
                <motion.div className="h-3 rounded-full bg-gradient-to-r from-gold/80 to-gold" initial={{ width: 0 }}
                  animate={{ width: `${Math.min(fund.subscription_pct || 0, 100)}%` }} transition={{ duration: 1 }}
                />
              </div>
              <div className="text-right text-xs text-gold mt-1 font-bold">{fund.subscription_pct}% subscribed</div>
            </div>

            {/* Key Metrics Row */}
            <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
              {[
                { label: "Investors", value: fund.investor_count, color: "text-white" },
                { label: "Called", value: `$${((fund.total_called || 0) / 1e3).toFixed(0)}K`, color: "text-blue-300" },
                { label: "Uncalled", value: `$${((fund.uncalled || 0) / 1e3).toFixed(0)}K`, color: "text-amber-300" },
                { label: "Deployed", value: `$${((fund.total_deployed || 0) / 1e3).toFixed(0)}K`, color: "text-emerald-300" },
                { label: "NAV", value: `$${((fund.nav || 0) / 1e3).toFixed(0)}K`, color: "text-gold" },
                { label: "MOIC", value: `${fund.moic || 0}x`, color: "text-white" },
              ].map((m) => (
                <div key={m.label} className="rounded-xl bg-white/8 border border-white/10 p-3 text-center">
                  <div className="text-[9px] text-white/50 uppercase tracking-wider font-semibold">{m.label}</div>
                  <div className={`text-lg font-black ${m.color}`}>{m.value}</div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Action Buttons */}
          <div className="flex gap-3 flex-wrap">
            <GoldButton onClick={() => setShowCallModal(true)} data-testid="capital-call-btn">
              <Banknote className="h-4 w-4 inline mr-1" /> Issue Capital Call
            </GoldButton>
            <button onClick={toggleAutoAllocate} data-testid="toggle-auto-allocate"
              className={`px-4 py-2 rounded-lg text-sm font-medium border transition-all flex items-center gap-2 ${
                fund.auto_allocate_enabled
                  ? "bg-emerald-50 border-emerald-200 text-emerald-700"
                  : "bg-gray-50 border-gray-200 text-gray-500"
              }`}>
              <Zap className="h-4 w-4" />
              Auto-Allocate: {fund.auto_allocate_enabled ? "ON" : "OFF"}
              <span className="text-[10px] opacity-70">({fund.max_auto_allocation_pct}% max)</span>
            </button>
          </div>

          {/* Subscriptions */}
          <SubscriptionsPanel fundId={fund.fund_id} headers={headers} onRefresh={fetchData} />

          {/* Allocations */}
          <AllocationsPanel fundId={fund.fund_id} headers={headers} />
        </>
      )}

      {funds.length === 0 && !showCreate && (
        <div className="text-center py-16">
          <Building2 className="h-12 w-12 text-[#ccc] mx-auto mb-3" />
          <p className="text-sm text-[#888] mb-4">No funds created yet</p>
          <GoldButton onClick={() => setShowCreate(true)}>Create Your First Fund</GoldButton>
        </div>
      )}

      {/* Create Fund Modal */}
      {showCreate && <CreateFundModal onClose={() => setShowCreate(false)} headers={headers} onCreated={() => { setShowCreate(false); fetchData(); }} />}

      {/* Capital Call Modal */}
      {showCallModal && fund && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="capital-call-modal">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl p-6 w-full max-w-md border border-cream-warm shadow-xl">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-[#0A0A0A]">Issue Capital Call</h3>
              <button onClick={() => setShowCallModal(false)} className="text-[#999] hover:text-[#333]"><X className="h-5 w-5" /></button>
            </div>
            <p className="text-sm text-[#666] mb-4">{fund.name} — {fund.investor_count} active investors</p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-[#333] mb-1">Call Percentage (%)</label>
              <input type="number" value={callPct} onChange={(e) => setCallPct(Number(e.target.value))} min={1} max={100}
                className="w-full rounded-lg border border-cream-warm px-4 py-2.5 text-sm focus:border-emerald-500 outline-none" data-testid="call-pct-input" />
              <p className="text-xs text-[#999] mt-1">Percentage of each investor's uncalled commitment</p>
            </div>
            <GoldButton onClick={issueCapitalCall} disabled={actionLoading} className="w-full" data-testid="confirm-capital-call">
              {actionLoading ? <Loader2 className="h-4 w-4 animate-spin inline mr-2" /> : null}
              Issue Call
            </GoldButton>
          </motion.div>
        </div>
      )}
    </div>
  );
}

function SubscriptionsPanel({ fundId, headers, onRefresh }) {
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviewing, setReviewing] = useState(null);

  useEffect(() => {
    axios.get(`${API}/funds/${fundId}/subscriptions`, { headers })
      .then(r => setSubs(r.data.subscriptions || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [fundId]);

  const reviewSub = async (commitmentId, status) => {
    setReviewing(commitmentId);
    try {
      await axios.post(`${API}/funds/${fundId}/subscriptions/${commitmentId}/review`, { status }, { headers });
      const res = await axios.get(`${API}/funds/${fundId}/subscriptions`, { headers });
      setSubs(res.data.subscriptions || []);
      onRefresh();
    } catch (err) { alert(err.response?.data?.detail || "Failed"); }
    finally { setReviewing(null); }
  };

  const subColors = {
    pending_approval: "bg-amber-100 text-amber-700",
    active: "bg-emerald-100 text-emerald-700",
    called: "bg-blue-100 text-blue-700",
    rejected: "bg-red-100 text-red-600",
  };

  return (
    <Card hover={false}>
      <h4 className="text-sm font-bold text-[#0A0A0A] uppercase tracking-wider mb-4 flex items-center gap-2">
        <Users className="h-4 w-4 text-gold-dark" /> Investor Subscriptions
      </h4>
      {loading ? (
        <div className="flex justify-center py-6"><Loader2 className="h-6 w-6 animate-spin text-emerald-700" /></div>
      ) : subs.length === 0 ? (
        <p className="text-sm text-[#888] text-center py-6">No subscriptions yet</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="subscriptions-table">
            <thead>
              <tr className="border-b border-cream-warm text-[10px] uppercase tracking-wider text-[#999]">
                <th className="text-left py-2">Investor</th>
                <th className="text-right py-2">Subscribed</th>
                <th className="text-right py-2">Called</th>
                <th className="text-right py-2">Distributed</th>
                <th className="text-center py-2">Status</th>
                <th className="text-right py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {subs.map((sub) => (
                <tr key={sub.commitment_id} className="border-b border-cream-warm/50 hover:bg-cream/50 transition-colors" data-testid={`sub-row-${sub.commitment_id}`}>
                  <td className="py-3">
                    <div className="font-medium text-[#333]">{sub.investor_name || "—"}</div>
                    <div className="text-xs text-[#999]">{sub.investor_email}</div>
                  </td>
                  <td className="py-3 text-right font-bold text-[#333]">${(sub.subscription_amount || 0).toLocaleString()}</td>
                  <td className="py-3 text-right text-[#666]">${(sub.called_amount || 0).toLocaleString()}</td>
                  <td className="py-3 text-right text-[#666]">${(sub.distributed_amount || 0).toLocaleString()}</td>
                  <td className="py-3 text-center">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${subColors[sub.status] || "bg-gray-100 text-gray-600"}`}>
                      {sub.status?.replace("_", " ").toUpperCase()}
                    </span>
                  </td>
                  <td className="py-3 text-right">
                    {sub.status === "pending_approval" && (
                      <div className="flex gap-1 justify-end">
                        <button onClick={() => reviewSub(sub.commitment_id, "active")} disabled={reviewing === sub.commitment_id}
                          data-testid={`approve-sub-${sub.commitment_id}`}
                          className="px-2 py-1 rounded-lg bg-emerald-50 text-emerald-600 text-xs hover:bg-emerald-100 transition-colors">
                          {reviewing === sub.commitment_id ? <Loader2 className="h-3 w-3 animate-spin" /> : <UserCheck className="h-3 w-3" />}
                        </button>
                        <button onClick={() => reviewSub(sub.commitment_id, "rejected")} disabled={reviewing === sub.commitment_id}
                          data-testid={`reject-sub-${sub.commitment_id}`}
                          className="px-2 py-1 rounded-lg bg-red-50 text-red-500 text-xs hover:bg-red-100 transition-colors">
                          <UserX className="h-3 w-3" />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

function AllocationsPanel({ fundId, headers }) {
  const [allocs, setAllocs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/funds/${fundId}/allocations`, { headers })
      .then(r => setAllocs(r.data.allocations || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [fundId]);

  return (
    <Card hover={false}>
      <h4 className="text-sm font-bold text-[#0A0A0A] uppercase tracking-wider mb-4 flex items-center gap-2">
        <Target className="h-4 w-4 text-gold-dark" /> Deal Allocations
      </h4>
      {loading ? (
        <div className="flex justify-center py-6"><Loader2 className="h-6 w-6 animate-spin text-emerald-700" /></div>
      ) : allocs.length === 0 ? (
        <p className="text-sm text-[#888] text-center py-6">No deal allocations yet. Allocate fund capital to deals from the pipeline.</p>
      ) : (
        <div className="space-y-2">
          {allocs.map((a) => (
            <div key={a.allocation_id} className="rounded-xl bg-cream border border-cream-warm p-4 flex items-center justify-between"
              data-testid={`alloc-${a.allocation_id}`}>
              <div>
                <div className="font-medium text-sm text-[#333]">{a.deal_number || a.lead_id?.slice(0, 8)}</div>
                <div className="text-xs text-[#888]">{LOAN_TYPE_LABELS[a.loan_type] || a.loan_type} — {a.property_address || "N/A"}</div>
              </div>
              <div className="text-right">
                <div className="font-bold text-emerald-600">${(a.amount || 0).toLocaleString()}</div>
                <div className={`text-[10px] font-bold ${a.status === "deployed" ? "text-blue-600" : "text-emerald-600"}`}>
                  {a.auto_allocated ? "AUTO" : ""} {a.status?.toUpperCase()}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function CreateFundModal({ onClose, headers, onCreated }) {
  const [form, setForm] = useState({
    name: "Imaani Capital Fund I",
    target_size: 20000000,
    fund_term_years: 5,
    investment_period_months: 24,
    harvest_period_months: 36,
    management_fee_pct: 2.0,
    preferred_return_pct: 8.0,
    carried_interest_pct: 20.0,
    min_subscription: 50000,
    max_auto_allocation_pct: 50,
    description: "",
  });
  const [loading, setLoading] = useState(false);

  const handleCreate = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/funds/create`, form, { headers });
      onCreated();
    } catch (err) { alert(err.response?.data?.detail || "Failed to create fund"); }
    finally { setLoading(false); }
  };

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const inputClass = "w-full rounded-lg border border-cream-warm px-3 py-2 text-sm focus:border-emerald-500 outline-none";
  const labelClass = "block text-xs font-medium text-[#555] mb-1";

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="create-fund-modal">
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl p-6 w-full max-w-lg border border-cream-warm shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-bold text-[#0A0A0A]">Create Fund</h3>
          <button onClick={onClose} className="text-[#999] hover:text-[#333]"><X className="h-5 w-5" /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className={labelClass}>Fund Name</label>
            <input value={form.name} onChange={e => set("name", e.target.value)} className={inputClass} data-testid="fund-name-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={labelClass}>Target Size ($)</label>
              <input type="number" value={form.target_size} onChange={e => set("target_size", Number(e.target.value))} className={inputClass} data-testid="fund-target-input" /></div>
            <div><label className={labelClass}>Min Subscription ($)</label>
              <input type="number" value={form.min_subscription} onChange={e => set("min_subscription", Number(e.target.value))} className={inputClass} /></div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div><label className={labelClass}>Term (Years)</label>
              <input type="number" value={form.fund_term_years} onChange={e => set("fund_term_years", Number(e.target.value))} className={inputClass} /></div>
            <div><label className={labelClass}>Investment Period (mo)</label>
              <input type="number" value={form.investment_period_months} onChange={e => set("investment_period_months", Number(e.target.value))} className={inputClass} /></div>
            <div><label className={labelClass}>Harvest Period (mo)</label>
              <input type="number" value={form.harvest_period_months} onChange={e => set("harvest_period_months", Number(e.target.value))} className={inputClass} /></div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div><label className={labelClass}>Mgmt Fee (%)</label>
              <input type="number" step="0.1" value={form.management_fee_pct} onChange={e => set("management_fee_pct", Number(e.target.value))} className={inputClass} /></div>
            <div><label className={labelClass}>Preferred Return (%)</label>
              <input type="number" step="0.1" value={form.preferred_return_pct} onChange={e => set("preferred_return_pct", Number(e.target.value))} className={inputClass} /></div>
            <div><label className={labelClass}>Carry (%)</label>
              <input type="number" step="0.1" value={form.carried_interest_pct} onChange={e => set("carried_interest_pct", Number(e.target.value))} className={inputClass} /></div>
          </div>
          <div><label className={labelClass}>Max Auto-Allocation (%)</label>
            <input type="number" value={form.max_auto_allocation_pct} onChange={e => set("max_auto_allocation_pct", Number(e.target.value))} className={inputClass} /></div>
          <div><label className={labelClass}>Description</label>
            <textarea value={form.description} onChange={e => set("description", e.target.value)} rows={2} className={inputClass} /></div>
        </div>
        <GoldButton onClick={handleCreate} disabled={loading} className="w-full mt-4" data-testid="submit-create-fund">
          {loading ? <Loader2 className="h-4 w-4 animate-spin inline mr-2" /> : null}
          Create Fund
        </GoldButton>
      </motion.div>
    </div>
  );
}
